import { useState } from 'react';

import { sortiereKandidaten } from '../../domain/candidateSort';
import { useT } from '../../i18n';
import { selectExam, useAppStore } from '../../store/useAppStore';
import { Dialog } from '../../ui/Dialog';
import { Icon } from '../../ui/Icon';
import { SortierWahl } from '../../ui/SortierWahl';
import styles from './Start.module.css';

/**
 * Wer wird bewertet? Der letzte Schritt des Startablaufs.
 *
 * Der QR-Knopf nimmt den oberen Teil ein, weil er der erwartete Weg ist: der Prüfling hält
 * seinen Ausweis hin, der Prüfer scannt. Die Liste ist der Ausweg für den vergessenen oder
 * unlesbaren Ausweis und darum zugeklappt.
 *
 * Der Scanner ist **kein** eigener Nachbau: der Knopf öffnet das vorhandene Scanner-Blatt
 * (`ScannerScreen`), das über `selectCandidateByCode` denselben Weg nimmt wie ein Scan mitten
 * in der Station. Bricht der Prüfer dort ab, steht dieses Blatt wieder da.
 */
export function CandidatePickDialog() {
  const t = useT();
  const exam = useAppStore(selectExam);
  const openSheet = useAppStore((s) => s.openSheet);
  const pickCandidate = useAppStore((s) => s.pickCandidate);
  /*
   * Dieselbe Ordnung wie in der Prüflingsliste, und **derselbe** gespeicherte Wert: wer dort
   * nach Matrikelnummer sortiert, findet hier dieselbe Reihenfolge vor. Zwei getrennte
   * Einstellungen wären zwei Orte, an denen dasselbe steht.
   */
  const sortierung = useAppStore((s) => s.candidateSort);
  const setSortierung = useAppStore((s) => s.setCandidateSort);
  const [offen, setOffen] = useState(false);
  const [suche, setSuche] = useState('');

  const gesucht = suche.trim().toLowerCase();
  // ⚠️ Gesucht wird über dasselbe wie in der Prüflingsliste: Name, Matrikelnummer,
  // Prüfungsnummer. Die Prüfungsnummer fehlte hier, obwohl die Zeile sie **anzeigt** — wer sie
  // abtippte, bekam eine leere Liste.
  const liste = gesucht
    ? exam.candidates.filter(
        (c) =>
          c.name.toLowerCase().includes(gesucht) ||
          c.matriculationNumber.toLowerCase().includes(gesucht) ||
          (c.examNo ?? '').toLowerCase().includes(gesucht),
      )
    : exam.candidates;
  const geordnet = sortiereKandidaten(liste, sortierung);

  return (
    <Dialog
      kicker={t.start.pickKicker}
      title={t.start.pickTitle}
      onClose={() => undefined}
      width={520}
      dismissible={false}
    >
      <button
        type="button"
        className={`tap ${styles.scanButton}`}
        onClick={() => openSheet({ kind: 'scanner' })}
      >
        <Icon name="qr" size={54} />
        {/* Eine Zeile genügt (Entscheidung des Nutzers, 2026-09-13). Die zweite sagte „QR-Code
            auf dem Ausweis des Prüflings" — der Code kommt aber gar nicht vom Ausweis, und
            die Zeile kostete Platz, den die Liste darunter braucht. */}
        <span className={styles.scanLabel}>{t.start.scan}</span>
      </button>

      <button
        type="button"
        className={`tap ${styles.disclosure}`}
        aria-expanded={offen}
        onClick={() => setOffen((o) => !o)}
      >
        <span>{t.start.fromList}</span>
        <span className={styles.chevron} data-open={offen}>
          <Icon name="chevronDown" size={15} />
        </span>
      </button>

      {offen && (
        <div className={styles.list}>
          <input
            className={styles.search}
            value={suche}
            onChange={(e) => setSuche(e.target.value)}
            placeholder={t.candidates.search}
            aria-label={t.candidates.search}
          />
          <SortierWahl wert={sortierung} onWahl={setSortierung} />
          <div className={`sc ${styles.rows}`}>
            {geordnet.map((c) => (
              <button
                key={c.id}
                type="button"
                className={`tap ${styles.row}`}
                onClick={() => void pickCandidate(c.id)}
              >
                <span className={styles.rowName}>{c.name}</span>
                {/* Die Prüfungsnummer führt, wenn es eine gibt — dieselbe Regel wie in der
                    Prüflingsliste. Sonst die Matrikelnummer. */}
                <span className={styles.rowNumber}>{c.examNo ?? c.matriculationNumber}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </Dialog>
  );
}
